import {put, call} from 'redux-saga/effects';
import Option from "../../entities/option";
import * as Actions from "../actions/optionActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type OptionAction = {
    optionId: string;
    option: Option;
    options: Option[];
};

export function* fetchOneOption(action: OptionAction) {
	try {
		const option: Option = yield call(api.findOne, 'option', action.optionId);

		// Now we can update the user interface
		yield put({type: Actions.OPTION_FETCH_ONE_SUCCESS, optionId: action.optionId, option});
	}
	catch(err) {
		yield put({type: Actions.OPTION_FETCH_ONE_FAILURE, optionId: action.optionId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {OptionAction} action
 */
export function* saveOption(action: OptionAction) {
    // console.debug('⮂ OPTION_SAVE saga', action.option?.id);
    
	if (action.option === undefined || action.option === null) {
		console.error('saveOption received a null');
		throw new Error('saveOption received a null');
	}

	if (!(action.option instanceof Option)) {
		action.option = new Option(action.option);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'option', action.option);
		if (addResult.isErr()) {
			console.error(`⮂ saveOption: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.OPTION_SAVE_FAILURE, option: action.option, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.OPTION_SAVE, option: action.option});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'option', action.option.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.OPTION_SAVE_FAILURE, option: action.option, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {OptionAction} action
 * @param {Option[]} action.options
 */
export function* saveManyOptions(action: OptionAction) {
    // console.debug('⮂ OPTION_SAVE_MANY saga', action.options.map(option => option.id).join(','));
    
    if (!Array.isArray(action.options)) {
        console.error('saveManyOptions expected an array, received', action.options);
        throw new Error('saveManyOptions expected an array');
    }

	try {
		const toSave: Option[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.options.length; i++) {
			const option = action.options[i];
			option.persisted = false;
			toSave.push(option);
		}

		const addResult = yield call(idb.add, 'option', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyOptions: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.OPTION_SAVE_MANY_FAILURE, options: action.options, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.OPTION_SAVE_MANY, options: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.OPTION_SAVE_MANY_FAILURE, options: action.options, status: 500, err});

		console.error(`Error saving ${action.options.length} options to database`, err);
	}
}

/**
 * @param {OptionAction} action
 */
export function* removeOption(action: OptionAction) {
    const userId = getCurrentUserId();
	if (!userId || action.option === undefined || action.option === null || !(action.option instanceof Option)) {
			console.error('removeOption expected type Option, received', action.option);
			throw new Error('removeOption expected type Option');
		}

	try {
		// Mark as deleted
		action.option.status = 'D';
		action.option.addedOn = newHRTimestamp();
		action.option.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'option', action.option);
		if (addResult.isErr()) {
			console.error(`⮂ removeOption: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.OPTION_REMOVE_FAILURE, option: action.option, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.OPTION_REMOVE, option: action.option});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.OPTION_REMOVE_FAILURE, option: action.option, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Option.
	*
	* @param {OptionAction} action
	*/
export function* restoreOption(action: OptionAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreOption expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.option === undefined || action.option === null) {
        console.error('restoreOption expected a Option, received null');
        throw new Error('restoreOption expected a Option, received null');
    }
    else if (!(action.option instanceof Option)) action.option = new Option(action.option); 
    
	try {
		// Mark as restored
		action.option.status = 'U';
		action.option.addedOn = newHRTimestamp();
		action.option.addedBy = userId;
		action.option.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'option', action.option);
		if (addResult.isErr()) {
			console.error(`⮂ restoreOption: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.OPTION_RESTORE_FAILURE, option: action.option, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.OPTION_RESTORE, option: action.option});
	}
	catch(err) {
		yield put({type: Actions.OPTION_RESTORE_FAILURE, option: action.option, status: 500, err});

		console.error('Error restoring from database', err);
	}
}